import {
  Control,
  Controller,
  useForm,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { Dialog } from "components/Dialog";
import { Button } from "components/Button";
import { useTranslation } from "react-i18next";
import { TextField } from "@mui/material";
import {
  WaterMeterAuthType,
  WaterMeterCommunicationType,
  WaterMeterLocationType,
  WaterMeterModelType,
  WaterMeterType,
} from "types/dto";
import { Select } from "components/Select";
import { SortObjectByPropName } from "utils/arrayTools";
import { Enum2Array } from "utils/enumTools";
import RegionAutoComplete from "components/SpecificAutoComplete/RegionAutoComplete";
import { useState } from "react";
import { TAddFormTypes } from "../index";
import { useRegion_v2 } from "contexts/regions_v2";

type TProps = {
  control: Control<TAddFormTypes>;
  watch: UseFormWatch<TAddFormTypes>;
  setValue: UseFormSetValue<TAddFormTypes>;
};

export function InfoStep(props: TProps) {
  const { control, watch, setValue } = props;

  const { selectedRegion } = useRegion_v2();
  const { t } = useTranslation();

  const renderDifferentConfigByAuthType = () => {
    const isABP = watch("config.authType") === WaterMeterAuthType.ABP;
    if (!isABP) {
      return (
        <>
          <Controller
            name={"config.devEUI"}
            control={control}
            rules={{
              required: isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("devEUI")}
                  placeholder={t("devEUI")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className="col-span-1"
                />
              );
            }}
          />
          <Controller
            name={"config.appEUI"}
            control={control}
            rules={{
              required: isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("appEUI")}
                  placeholder={t("appEUI")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className="col-span-1"
                />
              );
            }}
          />
          <Controller
            name={"config.loraAppKey"}
            control={control}
            rules={{
              required: isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("loraAppKey")}
                  placeholder={t("loraAppKey")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className="col-span-1"
                />
              );
            }}
          />
        </>
      );
    } else {
      return (
        <>
          <Controller
            name={"config.loraAppSKey"}
            control={control}
            rules={{
              required: !isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("loraAppSKey")}
                  placeholder={t("loraAppSKey")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"flex-1"}
                />
              );
            }}
          />
          <Controller
            name={"config.loraDeviceAddress"}
            control={control}
            rules={{
              required: !isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("loraDeviceAddress")}
                  placeholder={t("loraDeviceAddress")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"flex-1"}
                />
              );
            }}
          />
          <Controller
            name={"config.loraNetworkKey"}
            control={control}
            rules={{
              required: !isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("loraNetworkKey")}
                  placeholder={t("loraNetworkKey")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"flex-1"}
                />
              );
            }}
          />
        </>
      );
    }
  };

  return (
    <div className="flex flex-col w-full gap-y-3 px-2">
      <p className={"font-bold text-primary-80"}>{t("meterInfo")}</p>
      <div className={"grid grid-cols-3 gap-x-2 px-2 gap-y-3"}>
        <Controller
          name={"serialNo"}
          control={control}
          rules={{
            required: true,
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextField
                label={t("serialNo")}
                placeholder={t("serialNo")}
                size="small"
                {...field}
                error={Boolean(error)}
                className={"col-span-2"}
              />
            );
          }}
        />
        <Controller
          name={"productionYear"}
          control={control}
          rules={{
            required: true,
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextField
                label={t("productionYear")}
                type={"number"}
                placeholder={t("productionYear")}
                size="small"
                {...field}
                error={Boolean(error)}
                className={"col-span-1"}
              />
            );
          }}
        />
        <Controller
          name={"waterMeterType"}
          control={control}
          rules={{
            required: true,
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <Select
                {...field}
                size="small"
                onChange={(e) => {
                  field.onChange(e?.value);
                }}
                className="col-span-1"
                label={t("waterMeterType")}
                options={SortObjectByPropName(
                  Enum2Array(WaterMeterType),
                  "name"
                )}
                isError={!!error}
              />
            );
          }}
        />
      </div>
      <p className={"font-bold text-primary-80 mt-3"}>
        {t("meterLocationInfo")}
      </p>
      <div className={"grid grid-cols-3 gap-x-2 px-2 gap-y-3"}>
        <div className="col-span-3">
          <RegionAutoComplete
            onChange={(value) => {
              setValue("provinceId", value?.province?.id);
              setValue("cityId", value?.city?.id);
              setValue("jurisdictionId", value?.jurisdiction?.id);
            }}
            defaultValue={{
              provinceId: selectedRegion.province?.id,
              cityId: selectedRegion.city?.id,
              jurisdictionId: selectedRegion.jurisdiction?.id,
            }}
          />
        </div>
        <Controller
          name={"locationType"}
          control={control}
          rules={{
            required: true,
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <Select
                {...field}
                size="small"
                onChange={(e) => {
                  field.onChange(e?.value);
                }}
                className="col-span-1"
                label={t("waterMeterLocationType")}
                options={SortObjectByPropName(
                  Enum2Array(WaterMeterLocationType),
                  "name"
                ).filter(
                  (item) =>
                    String(item.value) !==
                    String(WaterMeterLocationType.Installed)
                )}
                isError={!!error}
              />
            );
          }}
        />
      </div>
      <p className={"font-bold text-primary-80 mt-3"}>
        {t("meterCommunicationInfo")}
      </p>
      <div className={"grid grid-cols-3 gap-x-2 gap-y-3 px-2"}>
        <Controller
          name={"config.authType"}
          control={control}
          rules={{
            required: true,
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <Select
                size="small"
                className="col-span-1"
                label={t("waterMeterAuthType")}
                options={SortObjectByPropName(
                  Enum2Array(WaterMeterAuthType),
                  "name"
                )}
                onChange={(e) => {
                  field.onChange(e?.value);
                }}
                initialValue={Enum2Array(WaterMeterAuthType).find(
                  (item) =>
                    (item.value as unknown as WaterMeterAuthType) ===
                    WaterMeterAuthType.OTAA
                )}
                hasInitialValue={false}
              />
            );
          }}
        />
        <Controller
          name={"config.encryptionkey"}
          control={control}
          rules={{
            required: true,
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <TextField
                label={t("encryptionkey")}
                placeholder={t("encryptionkey")}
                size="small"
                {...field}
                error={Boolean(error)}
                className="col-span-2"
              />
            );
          }}
        />
        {renderDifferentConfigByAuthType()}
      </div>
    </div>
  );
}
